import { useState, useEffect, useCallback, useMemo } from "react";
import { useCountryApi } from "../../../hooks/useCountryApi";
import { useResourceApi } from "../../../hooks/useResourceApi";
import { useBusinessUnitApi } from "../../../hooks/useBusinessUnitApi";

export const useAllResourcesViewModel = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [successModal, setSuccessModal] = useState(null);

  const {
    getCountries,
    cacheOperations: { readCountries, writeCountries },
  } = useCountryApi();

  const {
    getBusinessUnits,
    cacheOperations: { readBusinessUnits, writeBusinessUnits },
  } = useBusinessUnitApi();

  const {
    getResources,
    createResource: apiCreateResource,
    updateResource: apiUpdateResource,
    deleteResource,
    cacheOperations: { readResources, writeResources },
  } = useResourceApi();

  const fetchData = useCallback(async () => {
    if (loading) return;
    setLoading(true);
    setError(null);

    try {
      const [resourcesResult, countriesResult, businessUnitsResult] =
        await Promise.all([getResources(), getCountries(), getBusinessUnits()]);
      console.log("resourcesResult", resourcesResult);
      console.log("countriesResult", countriesResult);
      console.log("businessUnitsResult", businessUnitsResult);

      if (
        !resourcesResult.success ||
        !countriesResult.success ||
        !businessUnitsResult.success
      ) {
        throw new Error("Failed to fetch required data");
      }

      writeResources(resourcesResult.data);
      writeCountries(countriesResult.data);
      writeBusinessUnits(businessUnitsResult.data);
    } catch (err) {
      setError(err.message);
      console.error("Error fetching data:", err);
    } finally {
      setLoading(false);
    }
  }, [
    getResources,
    getCountries,
    writeResources,
    writeCountries,
    loading,
    writeBusinessUnits,
    getBusinessUnits,
  ]);

  useEffect(() => {
    fetchData();
  }, []);

  const handleCreateResource = useCallback(
    async (formData) => {
      try {
        const result = await apiCreateResource(formData);
        if (result.success) {
          // const resources = readResources();
          // writeResources([...resources, result.data]);
          setSuccessModal("Resource created successfully");
          return { success: true };
        } else {
          throw new Error(result.error);
        }
      } catch (error) {
        console.error("Error creating resource:", error);
        throw new Error(error.message);
      }
    },
    [apiCreateResource, readResources, writeResources]
  );

  const handleUpdateResource = useCallback(
    async (resourceId, formData) => {
      try {
        const result = await apiUpdateResource(resourceId, formData);
        if (result.success) {
          const resources = readResources();
          const updatedResources = resources.map((resource) =>
            resource.resource_id === resourceId ? result.data : resource
          );
          writeResources(updatedResources);
          setSuccessModal("Resource updated successfully");
          return { success: true };
        } else {
          throw new Error(result.error);
        }
      } catch (error) {
        console.error("Error updating resource:", error);
        throw new Error(error.message);
      }
    },
    [apiUpdateResource, readResources, writeResources]
  );

  const handleDeleteResource = useCallback(
    async (resource) => {
      try {
        const result = await deleteResource(resource.resource_id);
        if (result.success) {
          const resources = readResources();
          const filteredResources = resources.filter(
            (res) => res.resource_id !== resource.resource_id
          );
          writeResources(filteredResources);
          setSuccessModal("Resource deleted successfully");
        } else {
          setError(result.error);
        }
      } catch (err) {
        setError(err.message);
        console.error("Error deleting resource:", err);
      }
    },
    [deleteResource, readResources, writeResources]
  );

  const getCountryName = useMemo(
    () => (countryId) => {
      const countries = readCountries();
      const country = countries.find(
        (c) => c.country_id.toString() === countryId?.toString()
      );
      return country?.country_name || "Unknown";
    },
    [readCountries]
  );

  const getBusinessUnitName = useMemo(
    () => (businessUnitId) => {
      const businessUnits = readBusinessUnits();
      console.log("businessUnits", businessUnits);
      console.log("businessUnitId", businessUnitId);

      const businessUnit = businessUnits.find(
        (bu) => bu.business_unit_id.toString() === businessUnitId?.toString()
      );

      return businessUnit?.business_unit_name || "Unknown";
    },
    [readBusinessUnits]
  );

  const getReportingManagerName = useMemo(() => (reportingManagerId) => {
    const reportingManagers = readResources();
    console.log("reportingManagers", reportingManagers);
    console.log("reportingManagerId", reportingManagerId);
    const reportingManager = reportingManagers.find(
      (rm) => rm.resource_id.toString() === reportingManagerId?.toString()
    );
    console.log('resource_id', reportingManager);
    
    return reportingManager?.username || "Unknown"
  },
  [readResources]
);

  return {
    resources: readResources(),
    countries: readCountries(),
    reportingManagers: readResources(),
    businessUnits: readBusinessUnits(),
    loading,
    error,
    getCountryName,
    getBusinessUnitName,
    createResource: handleCreateResource,
    updateResource: handleUpdateResource,
    handleDeleteResource,
    getReportingManagerName,
    successModal,
    setSuccessModal,
    refetch: fetchData,
  };
};
